import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext'; 
import { apiService } from '../services/api'; 
import UploadWidget from '../widgets/UploadWidget'; 
import SearchWidget from '../widgets/SearchWidget';
import '../style/Dashboard.css';

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Initialize state
  const [dashboardData, setDashboardData] = useState({
    totalDocuments: 0,
    activeUsers: 0,
    recentActivity: []
  });
  const [isLoading, setIsLoading] = useState(true);
  const [activeWidget, setActiveWidget] = useState(null);

  // --- Fetch Data ---
  const fetchDashboardStats = useCallback(async () => {
    try {
      // Fetch Documents, Users, AND Activities
      const [documents, users, activities] = await Promise.all([
        apiService.getDocuments(),
        apiService.getUsers(),
        apiService.getActivityLogs() // Fetching the logs we created in Step 1
      ]);

      const totalDocsCount = documents.length;
      const activeUsersCount = users.filter(u => 
        u.status && u.status.toLowerCase() === 'active'
      ).length;

      // Take the top 5 most recent activities
      const recentActivities = activities.slice(0, 5);

      setDashboardData({
        totalDocuments: totalDocsCount,
        activeUsers: activeUsersCount,
        recentActivity: recentActivities
      });
    } catch (error) {
      console.error("Failed to load dashboard stats:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardStats();
  }, [fetchDashboardStats]);

  const closeWidget = () => setActiveWidget(null);

  const handleSearch = (searchCriteria) => {
    closeWidget();
    navigate('/documents', { state: { filters: searchCriteria } });
  };

  const handleUploadSuccess = () => {
    closeWidget();
    fetchDashboardStats();
  };

  const renderWidget = () => {
    switch (activeWidget) {
      case 'upload':
        return (
          <UploadWidget 
            onClose={closeWidget} 
            onUploadSuccess={handleUploadSuccess}
            userOrganisation={user?.organisation}
          />
        );
      case 'search':
        return (
          <SearchWidget 
            onClose={closeWidget} 
            onSearch={handleSearch} 
          />
        );
      default:
        return null;
    }
  };

  // Helper to get icon based on activity type
  const getActivityIcon = (type) => {
    switch(type) {
      case 'LOGIN': return '🔑';
      case 'UPLOAD': return '📤';
      case 'DELETE': return '🗑️';
      case 'DOWNLOAD': return '⬇️';
      default: return '📄';
    }
  };

  return (
    <div className="dashboard">
      
      {/* --- WIDGET OVERLAY --- */}
      {activeWidget && renderWidget()}

      {/* --- HERO ROW --- */}
      <div className="dashboard-hero-row">
        <div className="dashboard-header">
          <div className="header-content">
            <h1>Document Management Dashboard</h1>
            <p>Welcome back, {user?.username || 'User'}!</p>
          </div>
        </div>

        <div className="metrics-grid">
          <div className="metric-card split-layout">
            <div className="metric-half">
              <div className="metric-icon">📄</div>
              <h3>{isLoading ? '...' : dashboardData.totalDocuments}</h3>
              <span className="metric-label">Total Documents</span>
            </div>
            <div className="metric-divider"></div>
            <div className="metric-half">
              <div className="metric-icon">👥</div>
              <h3>{isLoading ? '...' : dashboardData.activeUsers}</h3>
              <span className="metric-label">Active Users</span>
            </div>
          </div>
        </div>
      </div>

      {/* --- MAIN CONTENT --- */}
      <div className="dashboard-content">
        
        {/* Quick Actions */}
        <div className="quick-actions-section">
          <h3>Quick Actions</h3>
          <div className="dashboard-action-buttons">
            <button className="action-btn secondary" onClick={() => setActiveWidget('upload')}>
              <span className="btn-icon">📤</span> Upload Document
            </button>
            <button className="action-btn secondary" onClick={() => setActiveWidget('search')}>
              <span className="btn-icon">🔍</span> Search Documents
            </button>
            <button className="action-btn secondary" onClick={() => navigate('/users')}>
              <span className="btn-icon">👥</span> Manage Users
            </button>
            <button className="action-btn secondary" onClick={() => navigate('/activity-log')}>
              <span className="btn-icon">📋</span> Activity Log
            </button>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="recent-activity-section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
            <h3>Recent Activity</h3>
            <button 
              className="view-all-link" 
              onClick={() => navigate('/activity-log')}
              style={{ background: 'none', border: 'none', color: '#4a90e2', cursor: 'pointer', fontSize: '14px' }}
            >
              View All &rarr;
            </button>
          </div>

          <div className="activity-list">
            {dashboardData.recentActivity.length > 0 ? (
              dashboardData.recentActivity.map((activity, index) => (
                <div key={index} className="activity-item">
                  <div className="activity-icon-box">
                    {getActivityIcon(activity.type)}
                  </div>
                  <div className="activity-details">
                    <div className="activity-header">
                      <span className="activity-user">{activity.user}</span>
                      <span className="activity-time">
                        {new Date(activity.timestamp).toLocaleDateString()}
                      </span>
                    </div>
                    <p className="activity-desc">
                      <strong>{activity.type}:</strong> {activity.description}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <p>No recent activity found.</p>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

export default Dashboard;
